import React, { createContext, useEffect, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import click from '../asset/sound/click.mp3';
import hover from '../asset/sound/hover.mp3';
import submit from '../asset/sound/submit.mp3';
import coin from '../asset/sound/coin.mp3';
import gem from '../asset/sound/gem.mp3';
import club from '../asset/sound/club.mp3';
import music1 from '../asset/sound/home.mp3';
import music2 from '../asset/sound/game1.mp3';
import music3 from '../asset/sound/game2.mp3';

const AudioManagementContext = createContext();

export const AudioManagementProvider = ({ children }) => {
  const isTouchDevice =
    'ontouchstart' in window || navigator.maxTouchPoints > 0;

  const [audioContext, setAudioContext] = useState(null);
  const [audioBuffers, setAudioBuffers] = useState({});

  useEffect(() => {
    const context = new AudioContext();
    setAudioContext(context);

    return () => {
      if (context) {
        context.close();
      }
    };
  }, []);

  // Function to load audio files into AudioBuffer
  const loadAudio = async (url) => {
    if (!audioContext) return null;
    try {
      const response = await fetch(url);
      const arrayBuffer = await response.arrayBuffer();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      return audioBuffer;
    } catch (error) {
      console.error('Error loading audio file:', error);
      return null;
    }
  };

  // Load all sounds on component mount
  useEffect(() => {
    const loadAllAudio = async () => {
      const audioFiles = {
        click,
        hover,
        submit,
        coin,
        gem,
        club,
        music1,
        music2,
        music3
      };

      const buffers = {};

      for (const [key, url] of Object.entries(audioFiles)) {
        buffers[key] = await loadAudio(url);
      }

      setAudioBuffers(buffers);
    };

    loadAllAudio();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioContext]);

  // Function to play audio using AudioContext
  const playAudio = (sound, volume = 0.03, callback) => {
    if (!audioContext || !audioBuffers[sound]) return callback && callback();
    if (sound === 'hover' && isTouchDevice) return callback && callback();

    const audioBuffer = audioBuffers[sound];
    const source = audioContext.createBufferSource();
    source.buffer = audioBuffer;

    // If music, loop it
    if (sound.startsWith('music')) {
      source.loop = true;
    }

    // Create a GainNode for volume control
    const gainNode = audioContext.createGain();
    gainNode.gain.setValueAtTime(volume, 0);
    source.connect(gainNode);
    gainNode.connect(audioContext.destination);

    source.start(0);
    callback && callback(source);
  };

  const playClick = (volume, callback) => playAudio('click', volume, callback);
  const playHover = (volume, callback) => playAudio('hover', volume, callback);
  const playSubmit = (volume, callback) =>
    playAudio('submit', volume, callback);
  const playCoin = (volume, callback) => playAudio('coin', volume, callback);
  const playGem = (volume, callback) => playAudio('gem', volume, callback);
  const playClub = (volume, callback) => playAudio('club', volume, callback);
  const playMusic1 = (volume, callback) =>
    playAudio('music1', volume, callback);
  const playMusic2 = (volume, callback) =>
    playAudio('music2', volume, callback);
  const playMusic3 = (volume, callback) =>
    playAudio('music3', volume, callback);

  return (
    <AudioManagementContext.Provider
      value={{
        playClick,
        playHover,
        playSubmit,
        playCoin,
        playGem,
        playClub,
        playMusic1,
        playMusic2,
        playMusic3
      }}
    >
      {children}
    </AudioManagementContext.Provider>
  );
};

AudioManagementProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export const useAudio = () => useContext(AudioManagementContext);
